import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { ArrowLeft, Globe, LogOut, Menu, Star, User as UserIcon, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { Logo } from "./Logo"
import { Button } from "./ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { api, clearSessionToken } from "@/lib/api"
import { useAuthSession } from "@/hooks/use-auth-session"
import { WalletDropdown } from "./WalletDropdown"
import NotificationBell from "./NotificationBell"
import { LanguageSwitcher } from "./LanguageSwitcher"
import { NetworkBadge } from "./NetworkBadge"
import { useNavigateBack } from "@/hooks/use-navigate-back"
import { useLanguage } from "@/hooks/use-language"
import { cn } from "@/lib/utils"
import { isActive, isTabRoute } from "./top-bar-utils"
import { isLoyaltyPointsEnabled } from "@/lib/feature-flags"
import { usePointsBalance } from "@/features/loyalty-points"
import { Skeleton } from "./ui/skeleton"
import {
  getDesktopLinks,
  getOverflowLinks,
  type NavSurface,
} from "./nav-config"

export type SiteHeaderProps = {
  variant?: NavSurface
}

function getMobileTitle(pathname: string, t: TFunction): string {
  if (!pathname || pathname === "/") return ""
  if (pathname === "/auth") return t("pages.auth")
  if (pathname === "/calendar") return t("pages.calendar")
  if (pathname === "/create") return t("pages.create")
  if (pathname === "/my-events") return t("pages.myEvents")
  if (pathname === "/profile") return t("pages.profile")
  if (pathname === "/explore") return t("pages.explore")
  if (pathname.startsWith("/event/") && pathname.endsWith("/settings")) {
    return t("pages.eventSettings")
  }
  if (pathname.startsWith("/event/")) return t("pages.event")
  return ""
}

function NavLinkButton({
  to,
  label,
  pathname,
  cta = false,
  onNavigate,
  className,
}: {
  to: string
  label: string
  pathname: string
  cta?: boolean
  onNavigate?: () => void
  className?: string
}) {
  const active = isActive(to, pathname)

  return (
    <Link
      to={to}
      prefetch="intent"
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
      className={cn(
        "shrink-0 rounded-lg px-2 py-2 text-sm font-medium transition-colors md:px-3",
        cta
          ? active
            ? "bg-primary text-primary-foreground shadow-sm"
            : "bg-primary text-primary-foreground hover:bg-primary/90"
          : active
            ? "bg-primary/10 text-primary"
            : "text-foreground-secondary hover:bg-secondary hover:text-foreground",
        className,
      )}
    >
      {label}
    </Link>
  )
}

export default function SiteHeader({ variant = "app" }: SiteHeaderProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const goBack = useNavigateBack()
  const { currentUser: user } = useAuthSession()
  const { current: currentLang, toggle: toggleLang } = useLanguage()
  const loyaltyEnabled = isLoyaltyPointsEnabled()
  const { data: pointsBalance, isLoading: pointsLoading } = usePointsBalance(
    loyaltyEnabled ? user?.id : undefined,
  )
  const [sheetOpen, setSheetOpen] = useState(false)

  const pathname = location.pathname
  const isMarketing = variant === "marketing"
  const onTabRoute = isTabRoute(pathname)
  const mobileTitle = getMobileTitle(pathname, t)
  const desktopLinks = getDesktopLinks(Boolean(user), variant, t)
  const overflowLinks = getOverflowLinks(t)

  const [lastPathname, setLastPathname] = useState(pathname)
  if (lastPathname !== pathname) {
    setLastPathname(pathname)
    setSheetOpen(false)
  }

  const handleLogout = async () => {
    try {
      await api.post<void>("/auth/logout")
      clearSessionToken()
      window.location.href = "/auth"
    } catch {
      clearSessionToken()
      window.location.href = "/auth"
    }
  }

  const closeSheet = () => setSheetOpen(false)

  const desktopNavAria = isMarketing
    ? t("landing.nav.ariaLabel")
    : t("nav.mainNavigation", { defaultValue: "Main navigation" })

  return (
    <header
      className={cn(
        "z-50 overflow-x-hidden",
        isMarketing
          ? "landing-nav fixed inset-x-0 top-0 transition-[background-color,box-shadow,border-color]"
          : "sticky top-0 border-b border-border bg-background/80 backdrop-blur-xl backdrop-saturate-150",
      )}
      style={isMarketing ? undefined : { paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div
        className={cn(
          "relative mx-auto flex items-center gap-2 px-4 md:gap-3",
          isMarketing
            ? "h-14 max-w-6xl gap-4 md:h-16"
            : "h-14 w-full max-w-[1920px]",
        )}
      >
        <div className="flex shrink-0 items-center">
          {isMarketing || onTabRoute ? (
            <Link
              to="/"
              className="flex shrink-0 items-center overflow-hidden"
              aria-label={isMarketing ? "Creatix" : t("nav.home")}
            >
              <Logo
                variant="icon"
                className={cn(
                  "md:hidden",
                  isMarketing ? "landing-nav-logo h-9 w-9" : "h-8 w-8",
                )}
              />
              <Logo
                className={cn(
                  "hidden md:block",
                  isMarketing && "landing-nav-logo h-8 w-auto",
                )}
              />
            </Link>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 md:hidden"
                onClick={() => goBack("/")}
                aria-label={t("nav.back")}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Link
                to="/"
                className="hidden shrink-0 items-center overflow-hidden md:flex"
                aria-label={t("nav.home")}
              >
                <Logo />
              </Link>
            </>
          )}
        </div>

        <nav
          className={cn(
            "max-md:hidden md:flex md:min-w-0 md:flex-1 md:items-center",
            isMarketing
              ? "md:justify-center md:gap-6 lg:gap-10"
              : "md:gap-1 md:overflow-x-auto md:scrollbar-hide",
          )}
          aria-label={desktopNavAria}
        >
          {desktopLinks.map((link) =>
            isMarketing ? (
              <Link
                key={link.path}
                to={link.path}
                className="landing-nav-link nav-link text-xs font-medium uppercase tracking-wider transition"
              >
                {link.label}
              </Link>
            ) : (
              <NavLinkButton
                key={link.path}
                to={link.path}
                label={link.label}
                pathname={pathname}
                cta={link.cta}
              />
            ),
          )}
        </nav>

        {!isMarketing && !onTabRoute && mobileTitle ? (
          <span className="pointer-events-none absolute left-1/2 max-w-[45%] -translate-x-1/2 truncate text-sm font-medium md:hidden">
            {mobileTitle}
          </span>
        ) : null}

        <div className="relative z-10 ml-auto flex shrink-0 items-center justify-end gap-2">
          {!isMarketing ? <NetworkBadge size="sm" className="md:hidden" /> : null}

          {isMarketing ? (
            <>
              <LanguageSwitcher className="hidden md:inline-flex" />
              <Button asChild variant="ghost" size="sm" className="hidden text-sm md:inline-flex">
                <Link to="/auth">{t("landing.nav.signIn")}</Link>
              </Button>
              <Button asChild size="sm" className="hidden rounded-full text-sm md:inline-flex">
                <Link to="/auth?redirect=/create">{t("landing.nav.createEvent")}</Link>
              </Button>

              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="landing-nav-menu-btn md:hidden"
                    aria-label={t("landing.nav.openMenu")}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  hideCloseButton
                  overlayClassName="landing-mobile-nav-overlay"
                  className="landing-mobile-nav-sheet w-[min(100vw,20rem)] border-0 p-0 gap-0"
                >
                  <SheetHeader className="landing-mobile-nav-header">
                    <Logo className="landing-nav-logo h-8 w-auto" />
                    <SheetClose asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="landing-nav-menu-btn landing-mobile-nav-close shrink-0"
                        aria-label={t("landing.nav.closeMenu")}
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </SheetClose>
                    <SheetTitle className="sr-only">{t("landing.nav.menuTitle")}</SheetTitle>
                  </SheetHeader>

                  <nav
                    className="landing-mobile-nav-links"
                    aria-label={t("landing.nav.mobileAriaLabel")}
                  >
                    {desktopLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={closeSheet}
                        className="landing-mobile-nav-link"
                      >
                        {link.label}
                      </Link>
                    ))}
                    <hr className="landing-mobile-nav-divider" />
                    <Link to="/auth" onClick={closeSheet} className="landing-mobile-nav-signin">
                      {t("landing.nav.signIn")}
                    </Link>
                  </nav>

                  <div className="landing-mobile-nav-cta">
                    <Button asChild className="w-full rounded-full">
                      <Link to="/auth?redirect=/create" onClick={closeSheet}>
                        {t("landing.nav.createEvent")}
                      </Link>
                    </Button>
                    <LanguageSwitcher className="w-full justify-center" />
                  </div>
                </SheetContent>
              </Sheet>
            </>
          ) : user ? (
            <>
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    aria-label={t("nav.openMenu", { defaultValue: "Open menu" })}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[min(100vw-2rem,320px)]">
                  <SheetHeader>
                    <SheetTitle>{t("nav.menu", { defaultValue: "Menu" })}</SheetTitle>
                  </SheetHeader>
                  <nav
                    className="mt-4 flex flex-col gap-1"
                    aria-label={t("nav.mobileNavigation", {
                      defaultValue: "Mobile navigation",
                    })}
                  >
                    {overflowLinks.map((link) => (
                      <NavLinkButton
                        key={link.path}
                        to={link.path}
                        label={link.label}
                        pathname={pathname}
                        onNavigate={closeSheet}
                        className="w-full text-left"
                      />
                    ))}
                    <div className="px-1 py-2">
                      <WalletDropdown />
                    </div>
                    <LanguageSwitcher className="justify-start px-3" />
                    <Button
                      type="button"
                      variant="ghost"
                      className="justify-start px-3 text-red-600 hover:text-red-600 dark:text-red-400"
                      onClick={() => {
                        closeSheet()
                        void handleLogout()
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {t("nav.signOut")}
                    </Button>
                  </nav>
                </SheetContent>
              </Sheet>

              <div className="hidden items-center gap-2 md:flex">
                {loyaltyEnabled && (
                  <Link
                    to="/points"
                    className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-foreground-secondary transition hover:bg-secondary hover:text-foreground"
                    title="My points"
                  >
                    {pointsLoading ? (
                      <Skeleton className="h-5 w-12" />
                    ) : (
                      <>
                        <Star className="h-4 w-4 text-yellow-500" aria-hidden />
                        <span>{pointsBalance?.balance ?? 0}</span>
                      </>
                    )}
                  </Link>
                )}
                <NotificationBell />
                <WalletDropdown />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="group flex h-9 w-9 items-center justify-center rounded-lg transition hover:bg-primary/10"
                      title={t("nav.profile")}
                      aria-label={t("nav.profile")}
                    >
                      <Avatar className="h-8 w-8 border border-border">
                        <AvatarImage src={user.avatarUrl ?? undefined} />
                        <AvatarFallback className="bg-primary/10 text-sm font-bold text-primary">
                          {(user.name || user.displayName || user.username)?.[0] || "U"}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[9999] w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex cursor-pointer items-center gap-2">
                        <UserIcon className="h-4 w-4" />
                        {t("nav.profile")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={toggleLang} className="cursor-pointer">
                      <Globe className="mr-2 h-4 w-4" />
                      {currentLang === "id" ? t("language.english") : t("language.indonesian")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => void handleLogout()}
                      className="cursor-pointer text-red-600 focus:bg-red-50 dark:text-red-400 dark:focus:bg-red-950"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {t("nav.signOut")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          ) : (
            <>
              <LanguageSwitcher className="hidden md:inline-flex" />
              <Button asChild variant="ghost" size="sm" className="hidden text-sm md:inline-flex">
                <Link to="/auth">{t("nav.signIn")}</Link>
              </Button>
              <Button asChild size="sm" className="hidden rounded-full text-sm md:inline-flex">
                <Link to="/auth?redirect=/create">{t("nav.createEvent")}</Link>
              </Button>
              <Button asChild size="sm" className="md:hidden">
                <Link to="/auth">{t("nav.signIn")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
